import React, { useState, useEffect } from "react";
import { Input } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";

/**
 * Catalogo de tipos de habilitacion (GET/POST /habilitacion/tipos).
 *  - SelectTipoHabilitacion: elegir el tipo al dar de alta o editar; con
 *    "Nuevo tipo..." se agrega al catalogo ahi mismo.
 *  - FiltroTipoHabilitacion: "Todos los tipos" o uno; para filtrar listas.
 *  - coincideTipo(h, filtro): si una habilitacion (o su id en `habilitacion`) es de ese tipo.
 */
const URL_TIPOS = `${process.env.REACT_APP_URL_HABILITACION}/tipos`;
const NUEVO = "__nuevo__";

const encabezados = () => ({ headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } });

// Se pide una vez por sesion; las altas se agregan aqui y avisan a los demas selects.
let cache = null;
let pendiente = null;
const suscritos = new Set();
const ordenar = (lista) => [...new Set(lista)].sort((a, b) => a.localeCompare(b));

function cargar() {
  if (cache) return Promise.resolve(cache);
  if (!pendiente) {
    pendiente = axios
      .get(URL_TIPOS, encabezados())
      .then((r) => {
        cache = ordenar((r.data || []).filter((t) => t.is_active !== "No").map((t) => t.nombre));
        return cache;
      })
      .catch(() => [])
      .finally(() => {
        pendiente = null;
      });
  }
  return pendiente;
}

export function useTiposHabilitacion() {
  const [tipos, setTipos] = useState(cache || []);
  useEffect(() => {
    let vigente = true;
    const avisar = (lista) => vigente && setTipos(lista);
    suscritos.add(avisar);
    cargar().then(avisar);
    return () => {
      vigente = false;
      suscritos.delete(avisar);
    };
  }, []);
  return tipos;
}

/** Pide el nombre, lo da de alta (o devuelve el que ya existe) y regresa el nombre. */
export async function altaTipoHabilitacion() {
  const { value } = await Swal.fire({
    title: "Nuevo tipo de habilitación",
    input: "text",
    inputPlaceholder: "Ej. PARCHE",
    showCancelButton: true,
    confirmButtonText: "Agregar",
    cancelButtonText: "Cancelar",
    inputValidator: (v) => (!String(v || "").trim() ? "Escribe el tipo" : undefined),
  });
  if (!value) return null;
  try {
    const { data } = await axios.post(URL_TIPOS, { nombre: value }, encabezados());
    cache = ordenar([...(cache || []), data.nombre]);
    suscritos.forEach((f) => f(cache));
    return data.nombre;
  } catch (error) {
    Swal.fire("No se pudo agregar el tipo", (error.response && error.response.data && error.response.data.message) || "", "error");
    return null;
  }
}

export function SelectTipoHabilitacion({ value, onChange, bsSize, required, conAlta = true }) {
  const tipos = useTiposHabilitacion();
  // Un tipo que ya no esta en el catalogo se sigue viendo para no perderlo al editar.
  const opciones = value && !tipos.includes(value) ? [value, ...tipos] : tipos;
  return (
    <Input
      type="select"
      bsSize={bsSize}
      required={required}
      value={value || ""}
      onChange={async (e) => {
        if (e.target.value !== NUEVO) return onChange(e.target.value);
        const nuevo = await altaTipoHabilitacion();
        if (nuevo) onChange(nuevo);
      }}
    >
      <option value="">Selecciona el tipo</option>
      {opciones.map((t) => (
        <option key={t} value={t}>
          {t}
        </option>
      ))}
      {conAlta ? <option value={NUEVO}>+ Nuevo tipo…</option> : null}
    </Input>
  );
}

export function FiltroTipoHabilitacion({ value, onChange, bsSize, style, todos = "Todos los tipos" }) {
  const tipos = useTiposHabilitacion();
  return (
    <Input type="select" bsSize={bsSize} style={style} value={value || ""} onChange={(e) => onChange(e.target.value)}>
      <option value="">{todos}</option>
      {tipos.map((t) => (
        <option key={t} value={t}>
          {t}
        </option>
      ))}
    </Input>
  );
}

/** Habilitacion (objeto, o arreglo poblado) de ese tipo; sin filtro, todas. */
export function coincideTipo(h, filtro) {
  if (!filtro) return true;
  const hab = Array.isArray(h) ? h[0] : h;
  return Boolean(hab && hab.tipo === filtro);
}
